import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Activity, AlertTriangle, ArrowRight, Bell, CheckCircle2, ClipboardList, Clock, DoorOpen, Megaphone,
  Music, Plus, ShieldCheck, Sparkles, Ticket, TrendingDown, TrendingUp, Trash2, Users,
} from 'lucide-react'
import { Link } from '../router.js'
import { Drawer, Select, adminMutate, apiGet, usePermissions, useToast } from '../admin/primitives.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { Chart, formatValue, seriesColor, type ChartKind, type ChartSeries } from './Chart.js'
import { useSoiree } from './SoireeData.js'
import type { ProgrammedDj } from './lib.js'
import {
  AIDE_SCANS, FreshnessLine, LiveNumber, QualityDot, QuietNumber, dureeFr, heureFr, honnete, tPlus,
  useNowMinute,
} from './lib.js'
import { shouldShowCurrentTimeMarker } from './business-night.js'
import { NuitBilletteries } from './NuitBilletteries.js'
import { afficherLaNuit, fraicheursDeLaNuit, horsDeLaSoiree, nuitFr, tauxFr, tuileDeLaFiche } from './nuit-billetteries-model.js'
import {
  bandeLisible, couleurCss, ecartAffichable, ecartContre, formeEffective, formeParDefaut, libelleEcart, libelleToutLaNuit,
  type Portee,
} from './analytics-nuit-model.js'

/**
 * WIDGETS DU COCKPIT SOIRÉE — chacun s'enregistre dans le Layout Engine.
 * RÈGLE UX : une métrique visible est explicable — les chiffres cliquables
 * ouvrent un drill-down réel (drawer analytique), jamais un clic mort.
 * RÈGLE DONNÉES : null = « — », jamais un faux zéro.
 */

/* ═══════════════ BILLETTERIE — vue d'ensemble ═══════════════ */

export function TicketsOverviewWidget() {
  const { detail, openDrill } = useSoiree()
  const stats = detail?.soiree.stats ?? null
  /*
   * La bande lumineuse ne tourne que pendant la nuit. La phase vient du
   * SERVEUR (nuit commerciale, coupure 08:00 ; en cours dès qu'un événement
   * rattaché a ouvert) — jamais d'un `Date.now()` local, qui ferait
   * scintiller une soirée terminée parce que le navigateur est resté ouvert.
   */
  const enCours = detail?.soiree.phase === 'ONGOING'
  /* SOIRÉE ASSOCIATION (20/10/2026) : la tuile compte toute la nuit, toutes
     billetteries (réglage). Le 09/09, elle affichait 0 devant 894 entrées. */
  const t = tuileDeLaFiche(detail)
  const nuitEntiere = t.mode === 'NUIT' && t.nuit !== null
  const fraicheurs = nuitEntiere ? fraicheursDeLaNuit(t.nuit, detail?.freshness) : null
  // Un événement de la même nuit pas rattaché : hors du total (et des tiroirs), mais dit.
  const hors = nuitEntiere ? horsDeLaSoiree(detail?.night, t.nuit) : null
  return (
    <div className="so-bignum lc-card lc-card--violet so-widget">
      <span className="lc-card__edge" aria-hidden="true" />
      <button type="button"
        className={`so-bignum__value so-bignum__value--dominant so-clickable lc-bignumber${enCours ? ' lc-bignumber--live' : ''}`}
        onClick={() => openDrill('scans')}
        aria-label={nuitEntiere ? 'Détail des entrées de la nuit' : 'Détail des billets scannés'}>
        <LiveNumber value={t.valeur} />
      </button>
      <span className="so-bignum__label">{t.libelle}</span>
      {/* Toute la nuit : l'âge de CHAQUE billetterie — le nombre vient surtout de Pretix. */}
      {fraicheurs
        ? <p className={`so-fresh${fraicheurs.perimee ? ' so-fresh--warn' : ''}`}><Clock size={13} aria-hidden="true" />{fraicheurs.texte}</p>
        : <FreshnessLine f={detail?.freshness ?? null} />}
      <div className="so-minitiles">
        <button type="button" className="so-minitile so-clickable" onClick={() => openDrill('sales')}>
          <strong><QuietNumber value={t.vendus} /></strong><span>vendus</span>
        </button>
        {/* « émis » et « invitations » n'existent que sur Shotgun : absents de la nuit entière. */}
        {nuitEntiere ? null : <span className="so-minitile"><strong><QuietNumber value={stats?.tickets_issued ?? null} /></strong><span>émis</span></span>}
        <span className="so-minitile">
          <strong>{tauxFr(t.taux)}</strong>
          <span>taux de scan</span>
        </span>
        {nuitEntiere ? null : <span className="so-minitile"><strong>{honnete(stats?.tickets_invitation)}</strong><span>invitations</span></span>}
        {/* Remboursés et annulés : ceux de Shotgun seul — sous un total de toute
            la nuit, « 1 annulé » se lirait comme pris sur ses 979 (revue du 20/10). */}
        {!nuitEntiere && stats && stats.tickets_refunded > 0
          ? <span className="so-minitile so-minitile--warn"><strong>{stats.tickets_refunded}</strong><span>remboursés</span></span> : null}
        {!nuitEntiere && stats && stats.tickets_cancelled > 0
          ? <span className="so-minitile so-minitile--warn"><strong>{stats.tickets_cancelled}</strong><span>annulés</span></span> : null}
      </div>
      {/* LA NUIT de cette soirée, toutes billetteries. Sur une soirée
          association, toujours : c'est elle qui explique le grand nombre
          (« INTERSANTE 0 · Shotgun / INTERSANTE (×8) 894 · Pretix »).
          Sinon, quand elle dit plus que la soirée et que le réglage l'autorise.
          Les lignes ouvrent les autres soirées de la nuit. */}
      {nuitEntiere ? (
        <div className="so-nuit--widget">
          <NuitBilletteries nuit={t.nuit!} sansTete liens />
          {hors ? <p className="lc-hint" style={{ margin: '4px 0 0' }}>{hors}</p> : null}
        </div>
      ) : detail && afficherLaNuit(detail.night, detail.billetteries, 'cockpit') && detail.night ? (
        <div className="so-nuit--widget">
          <p className="lc-hint" style={{ margin: '0 0 4px' }}>{nuitFr(detail.night.nuit)}, toutes billetteries</p>
          <NuitBilletteries nuit={detail.night} liens />
        </div>
      ) : null}
      <p className="lc-hint so-aide">{AIDE_SCANS}</p>
    </div>
  )
}

/* ═══════════════ OPÉRATIONS — projections cross-domain ═══════════════ */

/**
 * KPI d'opérations — une carte, un chiffre, une intention.
 *
 * L'ancienne liste de lignes techniques obligeait à scroller DANS la carte et
 * répétait « LIVE · 29 s » sur chaque ligne. Ici : des cartes qui respirent,
 * une seule fraîcheur en tête, et le détail par source relégué au drawer.
 */
function KpiCard({ icon, label, value, hint, warn, onDrill, drillLabel }: {
  icon: ReactNode
  label: string
  value: string
  hint?: string | null
  warn?: boolean
  onDrill?: (() => void) | undefined
  drillLabel?: string
}) {
  const body = (
    <>
      <span className="so-kpi__head">{icon}<span className="so-kpi__label">{label}</span></span>
      <strong className={`so-kpi__value${warn ? ' so-kpi__value--warn' : ''}`}>{value}</strong>
      {hint ? <span className="so-kpi__hint">{hint}</span> : null}
    </>
  )
  // clickable = useful : sans drill-down autorisé, aucun curseur trompeur.
  return onDrill
    ? <button type="button" className="so-kpi so-kpi--drill" onClick={onDrill}
        aria-label={drillLabel ?? `${label} — voir le détail`}>{body}</button>
    : <div className="so-kpi">{body}</div>
}

export function OperationsWidget() {
  const { detail, projection: p, openDrill } = useSoiree()
  const permissions = usePermissions()
  const toast = useToast()
  const { id, reloadProjection } = useSoiree()
  const [confirm, setConfirm] = useState(false)
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const peutMarquer = permissions.permissions.includes('soiree.manage')
  const phase = detail?.soiree.phase

  if (!p) {
    return (
      <section className="lc-panel gd-panel so-widget" aria-label="Opérations">
        <div className="lc-panel__body"><div className="so-skel" aria-busy="true"><span /><span /><span /></div></div>
      </section>
    )
  }

  /*
   * L'annonce vient du SERVEUR, résolue une fois pour toutes. Le widget
   * lisait auparavant `operational_summary`, une table calculée seulement à
   * la clôture : pendant la nuit elle est vide, et l'écran affirmait « Pas
   * encore donnée » alors que la Main Courante affichait déjà une heure.
   */
  const closing = p.closing_announcement
  const finished = p.mode === 'FINAL'
  const summary = p.operational_summary

  // FRAÎCHEUR GLOBALE, une seule fois (§9). En FINAL, plus aucun « LIVE ».
  const sources = [p.security, p.staff, p.infrastructure, p.access]
  const ages = sources.map((x) => x.seconds_since_success).filter((a): a is number => a !== null)
  const freshest = ages.length > 0 ? Math.min(...ages) : null
  const anyLate = sources.some((x) => x.quality === 'STALE' || x.quality === 'UNAVAILABLE')
  const ageTxt = freshest === null ? null
    : freshest < 120 ? `${freshest} s` : freshest < 7200 ? `${Math.round(freshest / 60)} min` : `${Math.round(freshest / 3600)} h`

  const staffTxt = p.staff.present_confirmed === null ? '—'
    : p.staff.present_confirmed === 0 && (p.staff.uncertain ?? 0) > 0 ? '—'
    : String(p.staff.present_confirmed)
  const notYet = (q: string) => q === 'NOT_YET_AVAILABLE'
  const agentsHint = notYet(p.security.quality) ? 'Pas encore en service à cette date'
    : finished && summary ? `au pic de la nuit${summary.security_agents_carded !== null ? ` · ${summary.security_agents_carded} agents fichés` : ''}`
    : p.security.quality === 'STALE' ? '⚠ donnée ancienne' : null

  const marquer = async () => {
    const r = await adminMutate<{ ok?: boolean; occurred_at?: string; error?: string }>(
      'POST', `/api/v1/soirees/${id}/markers`, { type: 'CLOSING_ANNOUNCEMENT' })
    if (r.status === 200) { toast('ok', `Annonce de fermeture enregistrée à ${heureFr(r.data.occurred_at!)}.`); reloadProjection() }
    else if (r.status === 409) { toast('error', 'Déjà enregistrée.'); reloadProjection() }
    else toast('error', 'Enregistrement impossible.')
  }

  return (
    <section className="lc-panel gd-panel so-widget so-widget--ops" aria-label="Opérations">
      <header className="lc-panel__head so-ops__head">
        <h2 className="lc-section-title"><ShieldCheck size={16} aria-hidden="true" /> Opérations</h2>
        <span className="so-ops__meta">
          {finished ? (
            <span className="so-quality so-quality--idle">
              Données finales{summary?.closed_at ? ` · clôture ${heureFr(summary.closed_at)}` : ''}
            </span>
          ) : freshest !== null ? (
            <span className={`so-quality ${anyLate ? 'so-quality--warn' : 'so-quality--live'}`}>
              {anyLate ? '⚠' : <span className="lc-dot lc-dot--ok lc-dot--live" />}
              {anyLate ? ' LIVE · certaines données en retard' : ` LIVE · actualisé il y a ${ageTxt}`}
            </span>
          ) : (
            <span className="so-quality so-quality--idle">Aucune donnée</span>
          )}
          <button type="button" className="lc-btn lc-btn--ghost so-ops__sources"
            onClick={() => setSourcesOpen(true)}>Détail des sources</button>
        </span>
      </header>

      <div className="lc-panel__body so-kpis">
        <KpiCard icon={<ShieldCheck size={15} aria-hidden="true" />}
          label={finished ? 'Effectif sécurité' : 'Agents sur place'}
          value={honnete(p.security.agents_on_duty)}
          hint={agentsHint}
          onDrill={p.can_drill.security ? () => openDrill('security') : undefined} />
        <KpiCard icon={<Users size={15} aria-hidden="true" />} label="Staff"
          value={staffTxt}
          hint={notYet(p.staff.quality) ? 'Pas encore en service à cette date'
            : (p.staff.uncertain ?? 0) > 0 ? `${p.staff.uncertain} à confirmer` : null}
          onDrill={p.can_drill.presence ? () => openDrill('staff') : undefined} />
        <KpiCard icon={<ClipboardList size={15} aria-hidden="true" />}
          label={finished ? 'Entrées main courante' : 'Incidents actifs'}
          value={honnete(p.security.open_incidents)}
          warn={!finished && (p.security.open_incidents ?? 0) > 0}
          hint={notYet(p.security.quality) ? 'Pas encore en service à cette date' : null}
          onDrill={p.can_drill.security ? () => openDrill('security') : undefined} />
        <KpiCard icon={<AlertTriangle size={15} aria-hidden="true" />} label="Critiques"
          value={honnete(p.security.critical_incidents)}
          warn={(p.security.critical_incidents ?? 0) > 0}
          hint={notYet(p.security.quality) ? 'Pas encore en service à cette date' : null} />
      </div>

      {/* ═══ ANNONCE DE FERMETURE — donnée opérationnelle majeure ═══ */}
      <div className={`so-closing ${closing ? 'so-closing--done' : ''}`}>
        <Megaphone size={16} aria-hidden="true" />
        <div className="so-closing__body">
          <span className="so-closing__label">Annonce de fermeture</span>
          {closing.state === 'RECORDED' && closing.at ? (
            <span className="so-closing__value">
              Donnée à {heureFr(closing.at)}
              {closing.recorded_by_name ? <em> · enregistrée par {closing.recorded_by_name}</em> : null}
            </span>
          ) : closing.state === 'PLANNED' && closing.at ? (
            /* « Annoncée POUR » — une heure prévue, pas un événement passé.
               Le mot compte : c'est ce qui distingue les deux faits. */
            <span className="so-closing__value">
              Annoncée pour {heureFr(closing.at)} <em>· d'après la Main Courante</em>
            </span>
          ) : closing.state === 'UNKNOWN' ? (
            /* ABSENT ≠ INCONNU : sans marqueur ET sans Main Courante
               joignable, on ne PEUT pas savoir — le dire vaut mieux
               qu'affirmer que rien n'a été annoncé. */
            <span className="so-closing__value so-closing__value--pending">
              Indisponible — Main Courante injoignable
            </span>
          ) : (
            <span className="so-closing__value so-closing__value--pending">
              {finished ? 'Non renseignée cette nuit-là' : 'Pas encore donnée'}
            </span>
          )}
        </div>
        {/* Le bouton enregistre l'instant où l'annonce est RÉELLEMENT faite
            au public. Une heure prévue par la Main Courante ne le remplace
            pas : « annoncée pour 05:30 » et « donnée à 04:12 » sont deux
            faits distincts, et c'est le second que ce marqueur consigne. */}
        {closing.state !== 'RECORDED' && peutMarquer && phase === 'ONGOING' ? (
          <button type="button" className="lc-btn lc-btn--primary" onClick={() => setConfirm(true)}>
            Marquer comme annoncée
          </button>
        ) : null}
      </div>

      {/* Fraîcheur PAR SOURCE : conservée, mais reléguée au drill-down (§10). */}
      <Drawer open={sourcesOpen} title="Détail des sources" onClose={() => setSourcesOpen(false)}>
        <dl className="lc-kv">
          {([
            ['Sécurité (Main Courante)', p.security],
            ['Présence du staff', p.staff],
            ['Infrastructure', p.infrastructure],
            ['Accès (UniFi)', p.access],
          ] as const).map(([label, src]) => (
            <div key={label}><dt>{label}</dt><dd><QualityDot s={src} /></dd></div>
          ))}
          {p.main_courante_link ? (
            <div><dt>Nuit Main Courante</dt><dd>
              rapprochement {p.main_courante_link.confidence === 'CONFIRMED' ? 'confirmé' : 'probable'}
              {p.main_courante_link.reason ? ` · ${p.main_courante_link.reason}` : ''}
            </dd></div>
          ) : null}
          {summary ? (
            <div><dt>Résumé de la nuit</dt><dd>
              calculé {heureFr(summary.computed_at)}
              {summary.security_agents_total !== null ? ` · ${summary.security_agents_total} personnes au total` : ''}
            </dd></div>
          ) : null}
        </dl>
        {p.source_coverage.filter((c) => c.state === 'NOT_YET_AVAILABLE').length > 0 ? (
          <div className="so-factors">
            <h3 className="so-factors__title">Sources absentes à cette date</h3>
            <ul>
              {p.source_coverage.filter((c) => c.state === 'NOT_YET_AVAILABLE').map((c) => (
                <li key={c.source}>{c.label} — pas encore en service</li>
              ))}
            </ul>
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirm}
        title="Annonce de fermeture"
        confirmLabel="C'est annoncé"
        onConfirm={() => { setConfirm(false); void marquer() }}
        onClose={() => setConfirm(false)}
      >
        <p>Enregistrer l'annonce de fermeture <strong>maintenant</strong> ({heureFr(new Date().toISOString())}) ?
          L'action est auditée ; l'heure reste corrigeable par un rôle autorisé.</p>
      </ConfirmDialog>
    </section>
  )
}

/* ═══════════════ SALLES & LINE-UP ═══════════════ */

/**
 * SALLES ET LINE-UP — une seule section, pas deux.
 *
 * Une passe précédente avait créé une section « Programmation » à côté de
 * « Salles » : deux concepts parallèles pour le même métier, alors que
 * « Salles » portait déjà la disposition ET les DJ. Les DJ ajoutés à la main
 * rejoignent donc la salle à laquelle ils appartiennent.
 *
 * Deux provenances cohabitent dans la même liste :
 *  · déduite (titre Shotgun, Main Courante) — non modifiable ici ;
 *  · manuelle — ajoutée et retirée ici.
 * La distinction se dit discrètement, sans badge criard.
 */
/**
 * SALLES — ON N'AFFICHE QUE CE QUI CONCERNE CETTE SOIRÉE.
 *
 * Le widget partait du RÉFÉRENTIEL de salles : une soirée sans programmation
 * affichait donc ROOM1, ROOM2, ROOM3, chacune vide, chacune avec son bouton.
 * Trois lignes de bruit et trois portes d'entrée pour une information qui
 * n'existait pas.
 *
 * Il part désormais des DONNÉES. Une salle n'apparaît que si quelque chose y
 * est programmé ; un artiste dont la salle est inconnue apparaît quand même,
 * dans « À assigner », parce qu'il joue bel et bien ce soir-là. Et il n'y a
 * qu'un seul bouton Ajouter.
 */
export function RoomsWidget() {
  const { id, projection: p, reloadProjection } = useSoiree()
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const toast = useToast()
  const lineup = p?.lineup ?? null

  const [manuel, setManuel] = useState<LineupEntry[]>([])
  const [salles, setSalles] = useState<{ code: string; label: string }[]>([])
  const [djs, setDjs] = useState<{ id: string; name: string }[]>([])
  const [ajoutOuvert, setAjoutOuvert] = useState(false)
  const [dj, setDj] = useState('')
  const [salleChoisie, setSalleChoisie] = useState('')
  const [busy, setBusy] = useState(false)
  /** Entrée dont on est en train de choisir la salle. */
  const [aAssigner, setAAssigner] = useState<string | null>(null)

  const charger = async () => {
    if (!id) return
    const j = await apiGet<{ lineup: LineupEntry[]; rooms: { code: string; label: string }[] }>(
      `/api/v1/soirees/${id}/lineup`).catch(() => null)
    if (j) { setManuel(j.lineup); setSalles(j.rooms) }
    // La composition des salles vit dans la projection : la relire est ce
    // qui fait apparaître (ou disparaître) un DJ tout juste modifié.
    reloadProjection()
  }
  useEffect(() => { void charger() }, [id])

  // Le référentiel DJ déjà livré — pas de liste locale parallèle (§16).
  useEffect(() => {
    void apiGet<{ djs: { id: string; name: string }[] }>('/api/v1/soirees/djs')
      .then((j) => setDjs(j.djs ?? [])).catch(() => setDjs([]))
  }, [])

  const ajouter = async () => {
    if (!id || !dj.trim()) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/soirees/${id}/lineup`,
      { dj_name: dj.trim(), room_code: salleChoisie === '' ? null : salleChoisie })
    setBusy(false)
    if (r.status === 201) {
      setDj(''); setSalleChoisie(''); setAjoutOuvert(false)
      toast('ok', 'Programmation mise à jour.'); await charger()
    } else toast('error', r.data?.message ?? 'Ajout impossible.')
  }

  const assigner = async (entryId: string, code: string) => {
    const r = await adminMutate<{ error?: string; message?: string }>(
      'PATCH', `/api/v1/soirees/lineup/${entryId}`, { room_code: code })
    setAAssigner(null)
    if (r.status === 200) { toast('ok', 'Salle assignée.'); await charger() }
    else toast('error', r.data?.message ?? 'Assignation impossible.')
  }

  const retirer = async (e: LineupEntry) => {
    const r = await adminMutate<{ error?: string; message?: string }>(
      'DELETE', `/api/v1/soirees/lineup/${e.id}`)
    if (r.status === 200) { toast('ok', `« ${e.dj_name} » retiré.`); await charger() }
    else toast('error', r.data?.message ?? 'Suppression impossible.')
  }

  /*
   * UNE SALLE = UNE LIGNE. La composition vient du SERVEUR
   * (`room_programming`) : programmation manuelle et observation Main
   * Courante rapportées à la même identité canonique. L'écran ne recompose
   * plus rien — c'est ce recalcul local qui faisait apparaître ROOM1 deux
   * fois, une source affirmant « DJ non renseigné » à côté du DJ connu.
   */
  const programmation = p?.room_programming ?? []
  const parSalle = programmation.filter((r) => r.code !== null)
  const sansSalle = programmation.filter((r) => r.code === null)
  const rien = programmation.length === 0

  const boutonAjouter = peutGerer ? (
    <button type="button" className="lc-btn so-rooms__add"
      onClick={() => { setAjoutOuvert((v) => !v); setDj(''); setSalleChoisie('') }}
      aria-expanded={ajoutOuvert}>
      <Plus size={14} aria-hidden="true" /> Ajouter
    </button>
  ) : null

  const formulaire = ajoutOuvert && peutGerer ? (
    <div className="so-rooms__form" role="group" aria-label="Ajouter à la programmation">
      <p className="so-rooms__form__title">Ajouter à la programmation</p>
      <label className="so-rooms__field">
        <span className="lc-hint">DJ</span>
        {/* Saisie libre ADOSSÉE au référentiel : on propose ce qui existe sans
            interdire un nom qui n'y est pas encore. */}
        <input className="lc-input" value={dj} list="so-djs" autoFocus
          maxLength={120} placeholder="Nom du DJ" aria-label="Nom du DJ"
          onChange={(e) => setDj(e.target.value)} />
        <datalist id="so-djs">
          {djs.map((d) => <option key={d.id} value={d.name} />)}
        </datalist>
      </label>
      <label className="so-rooms__field">
        <span className="lc-hint">Salle</span>
        <select className="lc-input" value={salleChoisie} aria-label="Salle"
          onChange={(e) => setSalleChoisie(e.target.value)}>
          <option value="">Non déterminée</option>
          {salles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
      </label>
      <div className="so-rooms__form__actions">
        <button type="button" className="lc-btn lc-btn--primary"
          disabled={busy || !dj.trim()} onClick={() => void ajouter()}>Ajouter</button>
        <button type="button" className="lc-btn lc-btn--ghost"
          onClick={() => setAjoutOuvert(false)}>Annuler</button>
      </div>
    </div>
  ) : null

  /* Une provenance ne se dit qu'UNE fois, en badge. Répéter la phrase entière
     sur chaque ligne transformait la carte en journal de synchronisation. */
  const LIBELLE_SOURCE: Record<string, string> = {
    MANUAL: 'Manuel',
    SHOTGUN_TITLE: 'Titre Shotgun',
    MAIN_COURANTE: 'Détectée via Main Courante',
  }

  const ligneDj = (dj: ProgrammedDj) => {
    const entree = dj.entry_id ? manuel.find((m) => m.id === dj.entry_id) : undefined
    return (
      <li className="so-room2__dj" key={dj.key}>
        <span className="so-room2__name">{dj.name}</span>
        {peutGerer && entree ? (
          <button type="button" className="lc-btn lc-btn--icon"
            aria-label={`Retirer ${dj.name}`} onClick={() => void retirer(entree)}>
            <Trash2 size={12} aria-hidden="true" />
          </button>
        ) : null}
      </li>
    )
  }

  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Salles et line-up">
      <header className="lc-panel__head">
        <h2 className="lc-section-title"><Music size={16} aria-hidden="true" /> Salles</h2>
        {!rien && lineup?.rooms_open != null ? (
          <span className="lc-pill lc-pill--ok">
            {lineup.rooms_open} salle{lineup.rooms_open > 1 ? 's' : ''} annoncée{lineup.rooms_open > 1 ? 's' : ''}
          </span>
        ) : null}
      </header>

      <div className="lc-panel__body so-rooms2">
        {rien ? (
          <p className="lc-hint so-rooms__vide">Aucune programmation renseignée.</p>
        ) : null}

        {parSalle.map((r) => (
          <div className={`so-room2${r.conflict ? ' so-room2--conflit' : ''}`} key={r.code}>
            <div className="so-room2__head">
              <span className="so-room2__code">{r.label}</span>
              {/* Les provenances de la SALLE, pas des lignes de programmation.
                  Une source qui atteste seulement « salle ouverte » apparaît
                  ici — elle n'a nommé aucun DJ. */}
              <span className="so-room2__sources">
                {r.room_sources.map((s) => (
                  <span className="so-room2__src" key={s}>{LIBELLE_SOURCE[s] ?? s}</span>
                ))}
              </span>
            </div>
            {r.conflict ? (
              <p className="so-room2__alerte">
                <AlertTriangle size={13} aria-hidden="true" />
                À vérifier — deux sources ne nomment pas le même DJ.
              </p>
            ) : null}
            <ul className="so-room2__list">
              {r.djs.length === 0 ? (
                <li className="so-room2__dj so-room2__dj--vide">
                  <span className="lc-hint">Salle ouverte, DJ non renseigné.</span>
                </li>
              ) : r.djs.map(ligneDj)}
            </ul>
          </div>
        ))}

        {/* §13 — l'artiste joue, mais on ignore où. Le taire serait pire. */}
        {sansSalle.length > 0 ? (
          <div className="so-room2 so-room2--orphelins">
            <div className="so-room2__head">
              <span className="so-room2__code">À assigner</span>
            </div>
            <ul className="so-room2__list">
              {sansSalle.flatMap((r) => r.djs).map((dj) => {
                const m = dj.entry_id ? manuel.find((x) => x.id === dj.entry_id) : undefined
                if (!m) {
                  return (
                    <li className="so-room2__dj" key={dj.key}>
                      <span className="so-room2__name">{dj.name}</span>
                      <em className="lc-hint">Salle non déterminée</em>
                    </li>
                  )
                }
                return (
                <li className="so-room2__dj" key={m.id}>
                  <span className="so-room2__name">{m.dj_name}</span>
                  {aAssigner === m.id ? (
                    <select className="lc-input" defaultValue="" autoFocus
                      aria-label={`Assigner une salle à ${m.dj_name}`}
                      onChange={(e) => { if (e.target.value) void assigner(m.id, e.target.value) }}>
                      <option value="" disabled>Choisir une salle…</option>
                      {salles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                    </select>
                  ) : (
                    <>
                      <em className="lc-hint">Salle non déterminée</em>
                      {peutGerer ? (
                        <button type="button" className="lc-btn lc-btn--ghost so-room2__assign"
                          onClick={() => setAAssigner(m.id)}>Assigner à une salle</button>
                      ) : null}
                    </>
                  )}
                </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        {formulaire}
        {!ajoutOuvert ? <div className="so-rooms__foot">{boutonAjouter}</div> : null}
      </div>
    </section>
  )
}

interface LineupEntry {
  id: string; dj_name: string; room_code: string | null; room_label: string | null
  origin: 'MANUAL' | 'AUTO'; created_at: string
}

/* ═══════════════ COMPARAISON HISTORIQUE ═══════════════ */

/* « N-1 » ne dit pas de quoi il s'agit : le même NUMÉRO de date un an plus tôt
   tombe presque toujours un autre jour de la semaine, et comparer un samedi à
   un dimanche n'a aucun sens pour un club. Le libellé le précise donc. */
const COMPARE_OPTIONS = [
  { value: 'SAME_WEEKDAY_N1', label: 'Même jour de semaine, N-1' },
  { value: 'SAME_WEEKDAY_N2', label: 'Même jour de semaine, N-2' },
  { value: 'SAME_WEEKDAY_N3', label: 'Même jour de semaine, N-3' },
  { value: 'SAME_DATE_N1', label: 'Même date exacte, N-1' },
  { value: 'PREVIOUS_COMPARABLE', label: 'Soirée précédente comparable' },
  { value: 'SAME_FAMILY', label: 'Événement de même famille' },
  { value: 'AVG_COMPARABLE', label: 'Moyenne des soirées comparables' },
]

function Delta({ pct }: { pct: number | null }) {
  if (pct === null) return null
  const up = pct >= 0
  return (
    <span className={`so-delta ${up ? 'so-delta--up' : 'so-delta--down'}`}>
      {up ? <TrendingUp size={13} aria-hidden="true" /> : <TrendingDown size={13} aria-hidden="true" />}
      {up ? '+' : ''}{pct.toLocaleString('fr-FR')} %
    </span>
  )
}

export function ComparisonWidget() {
  const { detail, comparison, compareMode, setCompareMode } = useSoiree()
  const c = comparison?.comparison ?? null
  const b = comparison?.benchmarks ?? null
  const [swap, setSwap] = useState(0)
  useEffect(() => { setSwap((x) => x + 1) }, [compareMode])

  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Comparaison historique">
      <header className="lc-panel__head">
        <h2 className="lc-section-title"><Activity size={16} aria-hidden="true" /> Performance</h2>
        <Select compact value={compareMode} options={COMPARE_OPTIONS}
          onChange={setCompareMode} ariaLabel="Mode de comparaison" />
      </header>
      <div className="lc-panel__body so-compare" key={swap}>
        {!c ? <p className="lc-hint">Chargement…</p> : c.reference === null ? (
          <p className="lc-hint">
            Aucune soirée comparable ({c.mode_label.toLowerCase()}) dans l'historique connu —
            rien n'est inventé.
          </p>
        ) : (
          <>
            <p className="so-compare__at">
              {/* Avant l'ouverture, « totaux finaux » serait faux : il n'y a pas
                  encore de total. La référence sert de repère, pas de verdict. */}
              {c.not_started
                ? 'Soirée à venir — repère historique'
                : c.elapsed_s !== null
                  ? <>À <strong>{tPlus(detail?.soiree.starts_at ?? '', new Date(detail?.soiree.starts_at ?? 0).getTime() + c.elapsed_s * 1000)}</strong> depuis le début</>
                  : 'Totaux finaux'}
              {c.reference.sample_size ? ` · moyenne de ${c.reference.sample_size} soirées` : ''}
            </p>
            <div className="so-compare__rows">
              <div className="so-compare__row">
                <span className="so-compare__metric">{c.cible === 'NUIT' ? 'Entrées de la nuit' : 'Billets scannés'}</span>
                <span className="so-compare__cur">{c.not_started ? '—' : honnete(c.current.scans)}</span>
                <span className="so-compare__ref">{honnete(c.reference.scans)}</span>
                <Delta pct={c.delta_scans_pct} />
              </div>
              <div className="so-compare__row">
                <span className="so-compare__metric">{c.cible === 'NUIT' ? 'Billets vendus, nuit' : 'Billets vendus'}</span>
                <span className="so-compare__cur">{c.not_started ? '—' : honnete(c.current.sales)}</span>
                <span className="so-compare__ref">{honnete(c.reference.sales)}</span>
                <Delta pct={c.delta_sales_pct} />
              </div>
            </div>
            {/* Même règle que le graphique de la page, et dite : la tuile ne
                donne plus un écart Shotgun quand le graphique donne celui de
                la nuit (revue du 19/10/2026). */}
            {c.phrase ? <p className="lc-hint so-compare__phrase">{c.phrase}</p> : null}
            <Link href={`/soirees/${c.reference.id}`} className="so-compare__reflink">
              Référence : {c.reference.name} · {new Date(c.reference.starts_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris' })}
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </>
        )}
        {b ? (
          b.clean_history && b.record_valid !== null ? (
            <div className="so-benchmarks">
              <span className="so-bench"><Sparkles size={13} aria-hidden="true" /> Record {b.family} : <strong>{b.record_valid}</strong> billets</span>
              {b.avg_valid !== null ? <span className="so-bench">Moyenne famille : <strong>{b.avg_valid}</strong></span> : null}
            </div>
          ) : (
            <p className="lc-hint so-benchhint">
              Records de la famille {b.family} indisponibles : l'historique
              complet n'est pas encore consolidé (pas de faux record).
            </p>
          )
        ) : null}
      </div>
    </section>
  )
}

/* ═══════════════ ANALYTICS EXPLORER ═══════════════ */

interface AnalyticsPayload {
  metric: string
  bucket_s: number
  group_by: string
  available: boolean
  /** Valeur faciale demandée sans `finance.live.read` : le serveur n'a envoyé aucun euro. */
  masked?: boolean
  event_starts_at: string
  series: { key: string; label: string; points: { t: string; value: number }[]; total: number }[]
  reference: {
    id: string; name: string; starts_at: string
    result: { available: boolean; series: { points: { t: string; value: number }[] }[] }
    /** Ce à quoi la référence se compare (19/10/2026) : toute la nuit, Shotgun seul, ou la soirée. */
    cible?: 'NUIT' | 'SHOTGUN' | 'SOIREE'
    phrase?: string | null
    cible_series?: { label: string; points: { t: string; value: number }[] } | null
    /** Sur toute la nuit : la référence compte aussi ses événements rattachés (nuit contre nuit). */
    night_with_associations?: boolean
  } | null
  /* Toute la nuit (19/10/2026) — absents d'un serveur plus ancien. */
  scope?: Portee
  scope_available?: boolean
  attached_count?: number
  anchor?: { id: string; name: string; source: string }
  colors?: Record<string, string>
  scope_note?: string
  /** `attendues` vaut `null` une fois la nuit finie : la phrase est alors un constat (revue du 19/10/2026). */
  bde_expected?: { attendues: number | null; terminee?: boolean; phrase: string } | { unavailable: string }
}

const METRIC_OPTIONS = [
  { value: 'scans', label: 'Billets scannés' },
  { value: 'sales', label: 'Billets vendus' },
  /* La somme des prix affichés des billets : une VALEUR FACIALE, jamais un
     chiffre d'affaires (15/10/2026). Le chiffre d'affaires de la nuit — arrêté
     à la clôture — se lit dans la tuile « Caisse de la nuit ». Proposée
     seulement avec `finance.live.read` : sans lui, le serveur n'envoie aucun
     euro. */
  { value: 'revenue', label: 'Valeur faciale des billets' },
]
const GROUP_OPTIONS = [
  { value: 'none', label: 'Sans ventilation' },
  { value: 'billetterie', label: 'Par billetterie' },
  { value: 'product', label: 'Par tarif' },
  { value: 'channel', label: 'Par canal' },
  { value: 'sale_mode', label: 'Prévente / Sur place' },
]
const BUCKET_OPTIONS = [
  { value: '60', label: 'Minute' },
  { value: '900', label: '15 min' },
  { value: '1800', label: '30 min' },
  { value: '3600', label: 'Heure' },
  { value: '86400', label: 'Jour' },
]
const KIND_OPTIONS: { value: ChartKind; label: string }[] = [
  { value: 'bars', label: 'Barres' },
  { value: 'grouped', label: 'Barres groupées' },
  { value: 'stacked', label: 'Barres empilées' },
  { value: 'line', label: 'Courbe' },
  { value: 'area', label: 'Aire' },
  { value: 'cumulative', label: 'Cumul' },
  { value: 'stacked_cumulative', label: 'Cumul empilé' },
]
const COMPARE_ANALYTICS = [
  { value: '', label: 'Sans référence' },
  { value: 'SAME_WEEKDAY_N1', label: 'vs même jour de semaine, N-1' },
  { value: 'SAME_WEEKDAY_N2', label: 'vs même jour de semaine, N-2' },
  { value: 'SAME_FAMILY', label: 'vs même famille' },
]

export function AnalyticsExplorerWidget({ config }: { config?: Record<string, unknown> }) {
  const { id, detail } = useSoiree()
  const permissions = usePermissions()
  const faciale = permissions.permissions.includes('finance.live.read')
  /* MAINTENANT — la phase vient du SERVEUR (projection canonique), jamais
     d'une comparaison de dates faite ici. L'horloge ne tourne que si le
     repère a une chance d'exister. */
  const situable = shouldShowCurrentTimeMarker(
    { startsAt: detail?.soiree.starts_at ?? null, phase: detail?.soiree.phase ?? null },
    Date.now(),
  )
  const nowMs = useNowMinute(situable)
  const [metric, setMetric] = useState(String(config?.['metric'] ?? 'scans'))
  // Une mesure réglée sur la valeur faciale reste affichée (vide, et dit pourquoi) : la retirer du menu laisserait un choix fantôme.
  const mesures = faciale || metric === 'revenue' ? METRIC_OPTIONS : METRIC_OPTIONS.filter((m) => m.value !== 'revenue')
  /* '' = AUTOMATIQUE : le serveur choisit (Réglages Soirées › Billetteries)
     et renvoie son choix, que les sélecteurs affichent. Sur une soirée avec
     un événement rattaché : toute la nuit, par billetterie, en cumul empilé,
     à l'heure pour des ventes de moins de quatre jours (19/10/2026). */
  const [groupBy, setGroupBy] = useState(String(config?.['group_by'] ?? ''))
  const [bucket, setBucket] = useState(String(config?.['bucket_s'] ?? ''))
  const [kind, setKind] = useState(String(config?.['kind'] ?? ''))
  const [scope, setScope] = useState<'' | Portee>('')
  /* §38 — LA RÉFÉRENCE N-1 EST LE DÉFAUT.
     Sans elle, Analytics ouvrait sur une courbe seule : utile pendant la
     soirée, muette avant. La référence est le même JOUR DE SEMAINE autour d'un
     an plus tôt — un samedi se compare à un samedi, jamais au même numéro de
     date qui tombera un mardi. L'utilisateur reste libre d'en changer. */
  const [compare, setCompare] = useState(String(config?.['compare'] ?? 'SAME_WEEKDAY_N1'))
  const [data, setData] = useState<AnalyticsPayload | null>(null)
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  // v4 : série « Prévision » optionnelle — une BANDE (fourchette), jamais
  // imposée dans tous les graphiques (§105-107).
  const [showForecast, setShowForecast] = useState(Boolean(config?.['forecast'] ?? false))
  const [trajectory, setTrajectory] = useState<{ elapsed_s: number; low: number; high: number }[] | null>(null)

  useEffect(() => {
    let vivant = true
    const params = new URLSearchParams({ metric })
    if (groupBy) params.set('group_by', groupBy)
    if (bucket) params.set('bucket_s', bucket)
    if (compare) params.set('compare', compare)
    if (scope) params.set('scope', scope)
    const charger = () => {
      fetch(`/api/v1/soirees/${id}/analytics?${params}`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((j: AnalyticsPayload) => { if (vivant) setData(j) })
        .catch(() => undefined)
    }
    charger()
    const t = setInterval(charger, 60_000)
    return () => { vivant = false; clearInterval(t) }
  }, [id, metric, groupBy, bucket, compare, scope])

  /* La prévision est celle de la SOIRÉE DU CLUB (l'ancre) : depuis MEDECINE,
     sur toute la nuit, c'est la bande de L'ÉTUDIANTE. Elle reste une
     prévision Shotgun — les soirées d'associations n'en ont pas. */
  const prevuePour = data?.anchor?.id ?? id
  useEffect(() => {
    if (!showForecast || metric !== 'scans') { setTrajectory(null); return }
    let vivant = true
    fetch(`/api/v1/soirees/${prevuePour}/forecast-snapshots`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { trajectory: { elapsed_s: number; low: number; high: number }[] | null } | null) => {
        if (vivant) setTrajectory(j?.trajectory ?? null)
      })
      .catch(() => undefined)
    return () => { vivant = false }
  }, [prevuePour, showForecast, metric])

  const portee: Portee = data?.scope ?? 'soiree'
  const ventilation = data?.group_by ?? (groupBy || 'none')
  const plusieursBilletteries = Object.keys(data?.colors ?? {}).length > 1
  const forme = kind || formeParDefaut(portee, ventilation)
  const nbSeries = (data?.series ?? []).filter((s) => !hiddenSeries.has(s.key)).length
  const effectiveKind = formeEffective(forme, ventilation, nbSeries) as ChartKind
  const empile = effectiveKind === 'stacked' || effectiveKind === 'stacked_cumulative'

  // Bande de prévision : trajectoire dérivée des courbes des comparables,
  // convertie sur l'axe temps réel de CETTE soirée. Sans trajectoire
  // honnête, aucune bande n'est dessinée (§108). Sur un cumul empilé, elle
  // se lit contre la couche du bas — Shotgun, la soirée du club ; sur une
  // seule courbe Shotgun + Pretix, elle n'a rien contre quoi se lire.
  const lisible = bandeLisible(portee, plusieursBilletteries, ventilation, effectiveKind)
  const forecastBand = useMemo(() => {
    if (!showForecast || !trajectory || trajectory.length < 2 || !data?.event_starts_at) return null
    if (!lisible.ok) return null
    const t0 = new Date(data.event_starts_at).getTime()
    return {
      label: plusieursBilletteries ? 'Prévision (Shotgun seul)' : 'Prévision',
      points: trajectory.map((p) => ({
        t: new Date(t0 + p.elapsed_s * 1000).toISOString(), low: p.low, high: p.high,
      })),
    }
  }, [showForecast, trajectory, data, lisible.ok, plusieursBilletteries])

  const couleurDe = (key: string, i: number): string =>
    (ventilation === 'billetterie' ? couleurCss(data?.colors?.[key]) : undefined) ?? seriesColor(i)

  const series: ChartSeries[] = useMemo(() => {
    if (!data) return []
    // On ne renonce plus dès que la soirée courante est vide : la référence
    // N-1 a ses propres données, et c'est précisément elle qu'on vient lire
    // avant une soirée.
    const out: ChartSeries[] = (data.available ? data.series : [])
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => !hiddenSeries.has(s.key))
      .map(({ s, i }) => ({ key: s.key, label: s.label, points: s.points, color: couleurDe(s.key, i) }))
    // Série de référence N-1 RÉALIGNÉE sur T+écoulé : chaque point de la
    // référence est translaté du décalage entre les débuts d'événements.
    if (data.reference?.result.available && !hiddenSeries.has('__ref')) {
      const shift = new Date(data.event_starts_at).getTime() - new Date(data.reference.starts_at).getTime()
      const pts = (data.reference.result.series[0]?.points ?? []).map((p) => ({
        t: new Date(new Date(p.t).getTime() + shift).toISOString(), value: p.value,
      }))
      out.push({ key: '__ref', label: `Référence · ${data.reference.name}`, points: pts, overlay: true, color: 'var(--text-2)' })
      // « Shotgun contre Shotgun » sans ventilation par billetterie : la part
      // Shotgun de cette année, jamais tracée, sert à l'écart.
      if (data.reference.cible === 'SHOTGUN' && ventilation !== 'billetterie' && data.reference.cible_series) {
        out.push({ key: '__cible', label: data.reference.cible_series.label, points: data.reference.cible_series.points, hidden: true })
      }
    }
    return out
  }, [data, hiddenSeries, ventilation]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSeries = (key: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }
  const allSeries = useMemo(() => {
    const base = (data?.series ?? []).map((s, i) => ({ key: s.key, label: s.label, total: s.total, color: couleurDe(s.key, i) }))
    if (data?.reference) base.push({ key: '__ref', label: 'Référence', total: 0, color: 'var(--text-2)' })
    return base
  }, [data, ventilation]) // eslint-disable-line react-hooks/exhaustive-deps

  const bde = data?.bde_expected
  /* Le repère « attendues » est une valeur PRETIX absolue : il se lit contre
     la courbe Pretix seule (cumul par billetterie), jamais contre une couche
     empilée sur Shotgun. Une nuit finie n'attend plus rien : pas de repère. */
  const attendues = bde && 'attendues' in bde && bde.attendues !== null && !bde.terminee ? bde.attendues : null
  const repereVisible = attendues !== null && !empile && ventilation === 'billetterie' && effectiveKind === 'cumulative'
  const guides = repereVisible
    ? [{ value: attendues, label: `Attendues (au moins)`, color: couleurCss(data?.colors?.['pretix']) ?? 'var(--cyan)' }]
    : undefined
  const contre = ecartContre(data?.reference?.cible, ventilation, !!data?.reference?.cible_series)
  /* Une série masquée dans la légende : l'écart ne compare plus la nuit. */
  const ecartOk = ecartAffichable(contre, hiddenSeries)

  // Les sélecteurs montrent ce que le serveur a tracé, même en automatique.
  const groupes = GROUP_OPTIONS.filter((g) => g.value !== 'billetterie' || plusieursBilletteries || ventilation === 'billetterie')
  const portees = [
    { value: 'nuit', label: libelleToutLaNuit(data?.attached_count ?? 0) },
    { value: 'soiree', label: 'Cette soirée seule' },
  ]

  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Explorateur analytique">
      <header className="lc-panel__head so-ax__head">
        <h2 className="lc-section-title"><Activity size={16} aria-hidden="true" /> Analytics</h2>
        <div className="so-ax__controls">
          {data?.scope_available && metric !== 'revenue' ? (
            <Select compact value={scope || portee} options={portees}
              onChange={(v) => { setScope(v === 'nuit' ? 'nuit' : 'soiree'); setGroupBy(''); setKind('') }} ariaLabel="Portée" />
          ) : null}
          <Select compact value={metric} options={mesures} onChange={setMetric} ariaLabel="Mesure" />
          <Select compact value={groupBy || ventilation} options={groupes} onChange={setGroupBy} ariaLabel="Ventilation" />
          <Select compact value={bucket || String(data?.bucket_s ?? '')} options={BUCKET_OPTIONS} onChange={setBucket} ariaLabel="Granularité" />
          <Select compact value={forme} options={KIND_OPTIONS} onChange={setKind} ariaLabel="Visualisation" />
          <Select compact value={compare} options={COMPARE_ANALYTICS} onChange={setCompare} ariaLabel="Référence" />
        </div>
      </header>
      <div className="lc-panel__body">
        {data === null ? <p className="lc-hint">Chargement…</p>
          /* « Aucune donnée » ne se dit que si NI la soirée, NI sa référence
             n'en ont. Sinon on masquait un historique bien réel. */
          : !data.available && !data.reference?.result.available ? (
            <p className="lc-hint">
              {metric === 'revenue' && data.masked
                ? 'La valeur faciale des billets demande le droit de lire la billetterie en direct : aucun montant n’est transmis à ce compte.'
                : metric === 'revenue'
                  ? 'Valeur faciale des billets non disponible : les prix ne sont pas renseignés de façon fiable sur ces billets.'
                  : 'Aucune donnée horodatée disponible pour cette mesure.'}
            </p>
          ) : (
            <>
              <Chart kind={effectiveKind} series={series} bucketS={data.bucket_s} band={forecastBand}
                nowMs={situable ? nowMs : null}
                guides={guides}
                ecartContre={contre}
                sansEcart={!ecartOk}
                ecartLibelle={libelleEcart(data.reference?.cible, compare, data.reference?.night_with_associations)}
                /* L'axe et le panneau parlent la langue de la MESURE : des
                   euros pour une valeur faciale, des entiers pour des
                   billets. Un « 1 250,00 » sur un axe de billets serait faux. */
                valueFormat={metric === 'revenue' ? 'currency' : 'integer'}
                ariaLabel={`${METRIC_OPTIONS.find((m) => m.value === metric)?.label}${portee === 'nuit' ? ', toute la nuit,' : ''} par ${data.bucket_s >= 86400 ? 'jour' : data.bucket_s >= 3600 ? 'heure' : `${data.bucket_s / 60} min`}`} />
              {metric === 'scans' ? (
                <div className="so-ax__legend">
                  <button type="button" className={`so-ax__serie ${showForecast ? '' : 'so-ax__serie--off'}`}
                    onClick={() => setShowForecast((v) => !v)} aria-pressed={showForecast}>
                    <span className="so-ax__dot" style={{ background: 'var(--amber)' }} aria-hidden="true" />
                    {plusieursBilletteries ? 'Prévision Shotgun (fourchette)' : 'Prévision (fourchette)'}
                  </button>
                  {showForecast && !forecastBand ? (
                    <span className="lc-hint">{lisible.raison ?? <>Pas de trajectoire prévisionnelle honnête pour cette soirée
                      {' '}(courbes comparables insuffisantes ou soirée passée).</>}</span>
                  ) : null}
                </div>
              ) : null}
              {bde ? (
                <p className="so-ax__ref so-ax__bde">
                  {'attendues' in bde ? bde.phrase : bde.unavailable}
                  {attendues !== null && !repereVisible ? (
                    /* Le repère ne se lit que sur la courbe Pretix seule : un clic l'y amène. */
                    <>{' '}<button type="button" className="so-ax__lien"
                      onClick={() => { setGroupBy('billetterie'); setKind('cumulative') }}>
                      Voir le repère sur la courbe
                    </button></>
                  ) : null}
                  {repereVisible ? ' Repère pointillé sur la courbe.' : null}
                </p>
              ) : null}
              {data.scope_note ? <p className="so-ax__ref">{data.scope_note}</p> : null}
              {data.reference ? (
                <p className="so-ax__ref">
                  {data.reference.phrase ? <>{data.reference.phrase}{' '}</> : null}
                  Référence :{' '}
                  <Link href={`/soirees/${data.reference.id}`}>
                    {data.reference.name}
                  </Link>
                  {' · '}
                  {new Date(data.reference.starts_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris' })}
                  {' — série réalignée sur T+écoulé'}
                </p>
              ) : null}
              {allSeries.length > 1 ? (
                <div className="so-ax__legend">
                  {allSeries.map((s) => (
                    <button key={s.key} type="button"
                      className={`so-ax__serie ${hiddenSeries.has(s.key) ? 'so-ax__serie--off' : ''}`}
                      onClick={() => toggleSeries(s.key)} aria-pressed={!hiddenSeries.has(s.key)}>
                      <span className="so-ax__dot" style={{ background: s.color }} aria-hidden="true" />
                      {/* Même formateur que l'axe et le panneau : un total en
                          centimes affiché comme des euros serait faux de deux
                          ordres de grandeur. */}
                      {s.label}{s.total > 0 ? ` · ${formatValue(s.total, metric === 'revenue' ? 'currency' : 'integer')}` : ''}
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          )}
      </div>
    </section>
  )
}

/* ═══════════════ TARIFS & CANAUX (drill-down par tarif) ═══════════════ */

export function ProductsWidget() {
  const { detail, openDrill } = useSoiree()
  const pct = (n: number | null) => (n === null ? '—' : `${n.toLocaleString('fr-FR')} %`)
  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Tarifs">
      <header className="lc-panel__head"><h2 className="lc-section-title"><Ticket size={16} aria-hidden="true" /> Tarifs</h2></header>
      <div className="lc-panel__body lc-panel__body--flush so-breakdown">
        {detail && detail.products.length > 0 ? detail.products.map((p) => (
          <button type="button" className="so-break so-break--drill" key={p.name}
            onClick={() => openDrill(`product:${p.name}`)} aria-label={`Détail du tarif ${p.name}`}>
            <div className="so-break__head">
              <span className="so-break__name">{p.name}{p.category ? <em> · {p.category}</em> : null}</span>
              <span className="so-break__nums">
                <strong>{p.tickets_scanned}</strong> / {p.tickets_valid}
                {p.scan_rate !== null ? <em>{pct(p.scan_rate)}</em> : null}
              </span>
            </div>
            <div className="so-break__bar" aria-hidden="true"><span style={{ width: `${p.scan_rate ?? 0}%` }} /></div>
          </button>
        )) : <p className="lc-hint so-pad">{detail ? 'Répartition non disponible.' : 'Chargement…'}</p>}
      </div>
    </section>
  )
}

export function ChannelsWidget() {
  const { detail } = useSoiree()
  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Canaux de vente">
      <header className="lc-panel__head"><h2 className="lc-section-title">Canaux de vente</h2></header>
      <div className="lc-panel__body lc-panel__body--flush so-breakdown">
        {detail && detail.channels.length > 0 ? detail.channels.map((c) => (
          <div className="so-break" key={c.channel}>
            <div className="so-break__head">
              <span className="so-break__name">{c.label}</span>
              <span className="so-break__nums"><strong>{c.tickets_scanned}</strong> / {c.tickets_valid} scannés</span>
            </div>
            <div className="so-break__bar" aria-hidden="true">
              <span style={{ width: `${c.tickets_valid > 0 ? Math.round((c.tickets_scanned / c.tickets_valid) * 100) : 0}%` }} />
            </div>
          </div>
        )) : <p className="lc-hint so-pad">{detail ? 'Canaux non disponibles.' : 'Chargement…'}</p>}
      </div>
    </section>
  )
}

/* ═══════════════ TIMELINE ═══════════════ */

const TIMELINE_ICONS: Record<string, typeof Sparkles> = {
  OPENING: DoorOpen, FIRST_SCAN: Ticket, SCAN_MILESTONE: Users,
  ALERT_FIRED: AlertTriangle, ALERT_ACKNOWLEDGED: ShieldCheck, ALERT_RESOLVED: CheckCircle2,
  CANCELLED: AlertTriangle, CLOSING_ANNOUNCEMENT: Megaphone,
  ROOM_OPENED: Music, ROOM_CLOSED: DoorOpen, ROOM_REOPENED: Music,
}

export function TimelineWidget() {
  const { detail } = useSoiree()
  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Timeline de la soirée">
      <header className="lc-panel__head">
        <h2 className="lc-section-title"><Bell size={16} aria-hidden="true" /> Timeline de la soirée</h2>
      </header>
      <div className="lc-panel__body lc-panel__body--flush so-timeline-scroll">
        {detail && detail.timeline.length > 0 ? (
          <ol className="so-timeline">
            {detail.timeline.map((e, i) => {
              const Icone = TIMELINE_ICONS[e.kind] ?? Sparkles
              return (
                <li key={`${e.at}-${i}`} className={`so-tl so-tl--${e.kind.toLowerCase()}`}>
                  <span className="so-tl__time">{heureFr(e.at)}</span>
                  <span className="so-tl__icon" aria-hidden="true"><Icone size={14} /></span>
                  <span className="so-tl__label">{e.label}</span>
                </li>
              )
            })}
          </ol>
        ) : <p className="lc-hint so-pad">{detail ? 'Rien à raconter pour le moment.' : 'Chargement…'}</p>}
      </div>
    </section>
  )
}

/* ═══════ mini-analytics de drawer (skeleton → contenu, jamais vide) ═══════ */

export function MiniAnalytics({ id, metric, groupBy, only, nuit = false }: {
  id: string
  metric: 'scans' | 'sales'
  groupBy: 'none' | 'product'
  /** Ne garder que cette série (drill-down d'un tarif précis). */
  only?: string
  /** Toute la nuit (la soirée et ses rattachés) : la tuile d'une soirée association la compte entière. */
  nuit?: boolean
}) {
  const { detail } = useSoiree()
  const situable = shouldShowCurrentTimeMarker(
    { startsAt: detail?.soiree.starts_at ?? null, phase: detail?.soiree.phase ?? null },
    Date.now(),
  )
  const nowMs = useNowMinute(situable)
  const [data, setData] = useState<AnalyticsPayload | null>(null)
  useEffect(() => {
    let vivant = true
    setData(null)
    /* La soirée SEULE (le tiroir détaille ses propres billets) — ou toute la
       nuit quand la tuile la compte (soirée association) ; le pas des ventes
       est celui du serveur : à l'heure sous quatre jours de vente. */
    const pas = metric === 'sales' ? '' : '&bucket_s=1800'
    fetch(`/api/v1/soirees/${id}/analytics?metric=${metric}&group_by=${groupBy}&scope=${nuit ? 'nuit' : 'soiree'}${pas}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setData(j) })
      .catch(() => undefined)
    return () => { vivant = false }
  }, [id, metric, groupBy, only, nuit])
  if (data === null) {
    return <div className="so-skel" aria-busy="true"><span /><span /><span /></div>
  }
  const series = (data.series ?? [])
    .filter((s) => !only || s.key === only)
    .map((s) => ({ key: s.key, label: s.label, points: s.points }))
  if (!data.available || series.length === 0 || series.every((s) => s.points.length === 0)) {
    return <p className="lc-hint">Aucun détail supplémentaire disponible pour cette donnée.</p>
  }
  return <Chart kind="cumulative" series={series} bucketS={data.bucket_s}
    nowMs={situable ? nowMs : null}
    ariaLabel={metric === 'scans' ? 'Scans cumulés' : 'Ventes cumulées'} />
}

/* ═══════════════ repères d'entrée (drawer scans) ═══════════════ */

export function ScanInsightsBlock({ id, nuit = false }: { id: string; nuit?: boolean }) {
  const [w, setW] = useState('900')
  const [d, setD] = useState<{
    first_scan: string | null; last_scan: string | null; duration_s: number | null
    peak: { window_s: number; scans: number; start: string } | null
  } | null>(null)
  useEffect(() => {
    let vivant = true
    // Toute la nuit : premier scan, pic et durée sur la soirée ET ses rattachés (09/09 : 00 h 09, 196 entrées en 15 min).
    fetch(`/api/v1/soirees/${id}/insights?window_s=${w}${nuit ? '&scope=nuit' : ''}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j) setD(j) })
      .catch(() => undefined)
    return () => { vivant = false }
  }, [id, w, nuit])
  if (!d) return <p className="lc-hint">Chargement…</p>
  return (
    <div className="so-insights">
      <div className="so-insight"><span>Premier scan</span><strong>{d.first_scan ? heureFr(d.first_scan) : '—'}</strong></div>
      <div className="so-insight"><span>Dernier scan</span><strong>{d.last_scan ? heureFr(d.last_scan) : '—'}</strong></div>
      <div className="so-insight"><span>Durée d'activité entrée</span><strong>{d.duration_s !== null ? dureeFr(d.duration_s) : '—'}</strong></div>
      <div className="so-insight so-insight--peak">
        <span>Pic d'entrée
          <Select compact value={w} ariaLabel="Fenêtre du pic"
            options={[{ value: '900', label: '15 min' }, { value: '1800', label: '30 min' }, { value: '3600', label: '60 min' }]}
            onChange={setW} />
        </span>
        <strong>
          {d.peak ? <>{d.peak.scans} scans · {heureFr(d.peak.start)} → {heureFr(new Date(new Date(d.peak.start).getTime() + d.peak.window_s * 1000).toISOString())}</> : '—'}
        </strong>
      </div>
    </div>
  )
}

/* ═══════════════ FORECAST (v4) — snapshots immuables ═══════════════ */

interface SnapshotView {
  id: string
  kind: 'LIVE_FORECAST' | 'BACKTEST'
  generated_at: string
  horizon_label: string
  predicted_low: number
  predicted_high: number
  predicted_center: number | null
  confidence: string
  presales_at_generation: number | null
  market_pressure: string | null
  market_signal_ids: string[]
  factors: { kind: string; label: string; detail: string }[]
}
interface SnapshotsPayload {
  finished: boolean
  live: SnapshotView[]
  backtest: SnapshotView[]
  outcome: {
    available: boolean; reason?: string
    final_snapshot: SnapshotView | null
    actual_scans: number | null; inside_range: boolean | null
    absolute_error: number | null; percentage_error: number | null
  } | null
  trajectory: { elapsed_s: number; low: number; high: number }[] | null
}

function useSnapshots(id: string): SnapshotsPayload | null {
  const [data, setData] = useState<SnapshotsPayload | null>(null)
  useEffect(() => {
    let vivant = true
    fetch(`/api/v1/soirees/${id}/forecast-snapshots`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setData(j) })
      .catch(() => undefined)
    return () => { vivant = false }
  }, [id])
  return data
}

const CONF_FR: Record<string, string> = { FAIBLE: 'faible', MOYENNE: 'moyenne', 'ÉLEVÉE': 'élevée' }
const PRESSURE_FR: Record<string, string> = { LOW: 'faible', MODERATE: 'modérée', HIGH: 'forte', VERY_HIGH: 'très forte' }

/** Prévision ACTUELLE d'une soirée à venir — une FOURCHETTE, jamais un
 *  chiffre précis inventé (§106). Sur soirée terminée : renvoie vers
 *  Prévision vs réel. */
export function ForecastCurrentWidget() {
  const { id, detail } = useSoiree()
  const phase = detail?.soiree.phase
  const [f, setF] = useState<{
    available: boolean; reason?: string
    range_low: number | null; range_high: number | null; confidence: string
    presales_at_milestone: number | null; milestone: string | null; presales_vs_ref_pct: number | null
    market_pressure: string | null
    reference_quality?: 'HIGH' | 'MEDIUM' | 'LOW' | 'VERY_LOW'
    family_refs_count?: number
    factors: { kind: string; label: string; detail: string }[]
    comparables: { id: string; name: string; starts_at: string; final_scans: number
      weight?: number; family_match?: boolean; atypical?: boolean }[]
  } | null>(null)
  const [why, setWhy] = useState(false)

  useEffect(() => {
    if (phase !== 'UPCOMING') return
    let vivant = true
    fetch(`/api/v1/soirees/${id}/forecast`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setF(j) })
      .catch(() => undefined)
    return () => { vivant = false }
  }, [id, phase])

  if (phase && phase !== 'UPCOMING') {
    return (
      <section className="lc-panel gd-panel so-widget" aria-label="Prévision">
        <header className="lc-panel__head"><h2 className="lc-section-title"><TrendingUp size={16} aria-hidden="true" /> Prévision</h2></header>
        <div className="lc-panel__body">
          <p className="lc-hint">
            {phase === 'ONGOING'
              ? 'Soirée en cours — la dernière prévision émise sera comparée au réel une fois la nuit terminée.'
              : 'Soirée terminée — voir le widget « Prévision vs réel ».'}
          </p>
        </div>
      </section>
    )
  }
  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Prévision actuelle">
      <header className="lc-panel__head"><h2 className="lc-section-title"><TrendingUp size={16} aria-hidden="true" /> Prévision</h2></header>
      <div className="lc-panel__body">
        {f === null ? <p className="lc-hint">Chargement…</p>
          : !f.available || f.range_low === null ? (
            <p className="lc-hint">{f.reason ?? 'Pas de prévision fiable pour cette soirée.'}</p>
          ) : (
            <>
              <p className="so-fc__range"><strong>{f.range_low}–{f.range_high}</strong> <span>scans projetés</span></p>
              <dl className="lc-kv">
                <div><dt>Confiance</dt><dd>{CONF_FR[f.confidence] ?? f.confidence}</dd></div>
                {/* Ici le jalon est à sa place : on explique le RYTHME, en
                    comparant au même repère que les références. Le libellé le
                    dit — « au jalon », et non « préventes » tout court. */}
                {f.presales_at_milestone !== null ? <div><dt>Préventes au jalon{f.milestone ? ` ${f.milestone}` : ''}</dt>
                  <dd>{f.presales_at_milestone}{f.presales_vs_ref_pct !== null ? ` · ${f.presales_vs_ref_pct >= 0 ? '+' : ''}${f.presales_vs_ref_pct.toLocaleString('fr-FR')} % vs référence` : ''}</dd></div> : null}
                {f.market_pressure ? <div><dt>Concurrence</dt><dd>{PRESSURE_FR[f.market_pressure] ?? f.market_pressure}</dd></div> : null}
                {typeof f.family_refs_count === 'number' ? (
                  <div><dt>Références</dt><dd>
                    {f.family_refs_count > 0 ? `${f.family_refs_count} soirée${f.family_refs_count > 1 ? 's' : ''} de la même famille` : 'aucune de la même famille'}
                    {f.reference_quality === 'LOW' || f.reference_quality === 'VERY_LOW' ? ' · données insuffisantes' : ''}
                  </dd></div>
                ) : null}
              </dl>
              <button type="button" className="lc-btn" onClick={() => setWhy(true)}>Pourquoi ?</button>
            </>
          )}
      </div>
      <Drawer open={why} title="Pourquoi cette prévision ?" onClose={() => setWhy(false)}>
        {f?.available ? (
          <>
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Fourchette <strong>{f.range_low}–{f.range_high} scans</strong> · confiance {CONF_FR[f.confidence] ?? f.confidence}.
              Méthode déterministe (comparables pondérés × rythme de préventes) — pas un intervalle statistique.
            </p>
            {(['POSITIF', 'NÉGATIF', 'NEUTRE'] as const).map((kind) => {
              const fs = (f.factors ?? []).filter((x) => x.kind === kind)
              if (fs.length === 0) return null
              return (
                <div key={kind} className="so-factors">
                  <h3 className={`so-factors__title so-factors__title--${kind.toLowerCase()}`}>
                    {kind === 'POSITIF' ? 'Facteurs positifs' : kind === 'NÉGATIF' ? 'Facteurs négatifs' : 'Contexte'}
                  </h3>
                  <ul>{fs.map((x) => <li key={x.label + x.detail}><strong>{x.label}</strong> — {x.detail}</li>)}</ul>
                </div>
              )
            })}
            {f.comparables.length > 0 ? (
              <div className="so-factors">
                <h3 className="so-factors__title">Soirées de référence</h3>
                <ul>{f.comparables.map((c) => (
                  <li key={c.id}>
                    <Link href={`/soirees/${c.id}`}>{c.name}</Link> · {c.final_scans} scans
                    {c.family_match === false ? ' · générique (même jour)' : ''}
                    {typeof c.weight === 'number' ? ` · poids ${c.weight}` : ''}
                    {c.atypical ? <span className="fam-atypique"> ATYPIQUE</span> : null}
                  </li>
                ))}</ul>
              </div>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </section>
  )
}

/** Soirée terminée : la dernière prévision émise AVANT ouverture face au
 *  réel — les mauvaises prédictions s'affichent comme les bonnes (§114). */
export function ForecastVsActualWidget() {
  const { id, detail } = useSoiree()
  const data = useSnapshots(id)
  const phase = detail?.soiree.phase
  if (phase && phase !== 'FINISHED') {
    return (
      <section className="lc-panel gd-panel so-widget" aria-label="Prévision vs réel">
        <header className="lc-panel__head"><h2 className="lc-section-title"><CheckCircle2 size={16} aria-hidden="true" /> Prévision vs réel</h2></header>
        <div className="lc-panel__body"><p className="lc-hint">Disponible une fois la soirée terminée.</p></div>
      </section>
    )
  }
  const o = data?.outcome ?? null
  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Prévision vs réel">
      <header className="lc-panel__head"><h2 className="lc-section-title"><CheckCircle2 size={16} aria-hidden="true" /> Prévision vs réel</h2></header>
      <div className="lc-panel__body">
        {data === null ? <p className="lc-hint">Chargement…</p>
          : !o || !o.available || !o.final_snapshot ? (
            <p className="lc-hint">
              {o?.reason ?? 'Aucune prévision historique disponible pour cette soirée.'}
              {data.backtest.length > 0 ? ' Une simulation rétrospective existe (voir Historique des prévisions) — elle n’est jamais confondue avec une prévision réellement émise.' : ''}
            </p>
          ) : (
            <dl className="lc-kv">
              <div><dt>Dernière prévision avant ouverture ({o.final_snapshot.horizon_label})</dt>
                <dd>{o.final_snapshot.predicted_low}–{o.final_snapshot.predicted_high} scans</dd></div>
              <div><dt>Résultat réel</dt><dd><strong>{o.actual_scans}</strong> scans</dd></div>
              <div><dt>Dans la fourchette</dt>
                <dd className={o.inside_range ? 'so-up' : 'so-down'}>{o.inside_range ? 'Oui' : 'Non'}</dd></div>
              <div><dt>Écart centre / réel</dt>
                <dd>{o.absolute_error !== null ? `${o.absolute_error >= 0 ? '+' : ''}${o.absolute_error}` : '—'}
                  {o.percentage_error !== null ? ` (${o.percentage_error >= 0 ? '+' : ''}${o.percentage_error.toLocaleString('fr-FR')} %)` : ''}</dd></div>
              <div><dt>Confiance annoncée</dt><dd>{CONF_FR[o.final_snapshot.confidence] ?? o.final_snapshot.confidence}</dd></div>
            </dl>
          )}
      </div>
    </section>
  )
}

/** Historique des prévisions : chaque snapshot IMMUABLE, du J-30 au réel.
 *  LIVE_FORECAST et SIMULATION RÉTROSPECTIVE jamais mélangés (§123). */
export function ForecastHistoryWidget() {
  const { id, detail } = useSoiree()
  const data = useSnapshots(id)
  const [sel, setSel] = useState<SnapshotView | null>(null)
  const actual = detail?.soiree.stats?.tickets_scanned ?? null
  const finished = data?.finished ?? false

  const rows = data?.live ?? []
  const back = data?.backtest ?? []
  const all = [...rows, ...(finished && rows.length === 0 ? back : [])]
  const scaleMax = Math.max(...all.map((s) => s.predicted_high), actual ?? 0, 1)
  const scaleMin = Math.min(...all.map((s) => s.predicted_low), actual ?? Infinity, scaleMax)
  const span = Math.max(scaleMax - scaleMin, 1)
  const pct = (v: number) => `${Math.round(((v - scaleMin) / span) * 100)}%`

  const prevOf = (s: SnapshotView): SnapshotView | null => {
    const i = rows.findIndex((x) => x.id === s.id)
    return i > 0 ? rows[i - 1]! : null
  }

  const renderRows = (list: SnapshotView[], backtest: boolean) => (
    <div className="so-fch" role="list">
      {list.map((s) => (
        <button key={s.id} type="button" role="listitem" className="so-fch__row so-clickable"
          onClick={() => setSel(s)} aria-label={`Prévision ${s.horizon_label} : ${s.predicted_low} à ${s.predicted_high} scans`}>
          <span className="so-fch__label">{s.horizon_label === 'DELTA'
            ? new Date(s.generated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' })
            : s.horizon_label}</span>
          <span className="so-fch__track" aria-hidden="true">
            <span className={`so-fch__band${backtest ? ' so-fch__band--bt' : ''}`}
              style={{ left: pct(s.predicted_low), width: `calc(${pct(s.predicted_high)} - ${pct(s.predicted_low)})` }} />
            {actual !== null && finished ? <span className="so-fch__actual" style={{ left: pct(actual) }} /> : null}
          </span>
          <span className="so-fch__nums">{s.predicted_low}–{s.predicted_high}</span>
        </button>
      ))}
      {actual !== null && finished ? (
        <div className="so-fch__row so-fch__row--real" role="listitem">
          <span className="so-fch__label">RÉEL</span>
          <span className="so-fch__track" aria-hidden="true">
            <span className="so-fch__actual so-fch__actual--big" style={{ left: pct(actual) }} />
          </span>
          <span className="so-fch__nums"><strong>{actual}</strong></span>
        </div>
      ) : null}
    </div>
  )

  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Historique des prévisions">
      <header className="lc-panel__head"><h2 className="lc-section-title"><Activity size={16} aria-hidden="true" /> Historique des prévisions</h2></header>
      <div className="lc-panel__body">
        {data === null ? <p className="lc-hint">Chargement…</p>
          : rows.length === 0 && back.length === 0 ? (
            <p className="lc-hint">Aucune prévision historique disponible pour cette soirée.</p>
          ) : (
            <>
              {rows.length > 0 ? renderRows(rows, false) : null}
              {rows.length === 0 && back.length > 0 ? (
                <>
                  <p className="so-fch__bt-title">SIMULATION RÉTROSPECTIVE — calculée après coup avec cutoff temporel,
                    jamais une prévision réellement émise.</p>
                  {renderRows(back, true)}
                </>
              ) : null}
              <p className="lc-hint">Chaque prévision est figée au moment où elle a été émise — jamais réécrite avec ce qu'on a appris ensuite.</p>
            </>
          )}
      </div>
      <Drawer open={sel !== null} title={sel ? `Prévision ${sel.horizon_label === 'DELTA' ? 'révisée' : sel.horizon_label}${sel.kind === 'BACKTEST' ? ' · SIMULATION RÉTROSPECTIVE' : ''}` : ''} onClose={() => setSel(null)}>
        {sel ? (() => {
          const prev = prevOf(sel)
          const center = (s: SnapshotView) => s.predicted_center ?? Math.round((s.predicted_low + s.predicted_high) / 2)
          return (
            <>
              <dl className="lc-kv">
                <div><dt>Émise le</dt><dd>{new Date(sel.generated_at).toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short', timeZone: 'Europe/Paris' })}</dd></div>
                <div><dt>Fourchette</dt><dd>{sel.predicted_low}–{sel.predicted_high} scans</dd></div>
                <div><dt>Confiance</dt><dd>{CONF_FR[sel.confidence] ?? sel.confidence}</dd></div>
                {sel.presales_at_generation !== null ? <div><dt>Préventes à ce moment</dt><dd>{sel.presales_at_generation}</dd></div> : null}
                {sel.market_pressure ? <div><dt>Pression marché</dt><dd>{PRESSURE_FR[sel.market_pressure] ?? sel.market_pressure}</dd></div> : null}
                {sel.market_signal_ids.length > 0 ? <div><dt>Signaux de veille connus</dt><dd>{sel.market_signal_ids.length}</dd></div> : null}
              </dl>
              {prev ? (
                <div className="so-factors">
                  <h3 className="so-factors__title">Depuis la prévision précédente ({prev.horizon_label})</h3>
                  <p className="lc-hint" style={{ margin: 0 }}>
                    Centre : {center(prev)} → {center(sel)} ({center(sel) - center(prev) >= 0 ? '+' : ''}{center(sel) - center(prev)} scans)
                    {prev.confidence !== sel.confidence ? ` · confiance ${CONF_FR[prev.confidence]} → ${CONF_FR[sel.confidence]}` : ''}
                  </p>
                </div>
              ) : null}
              {sel.factors.length > 0 ? (
                <div className="so-factors">
                  <h3 className="so-factors__title">Facteurs au moment de l'émission</h3>
                  <ul>{sel.factors.map((x, i) => <li key={i}><strong>{x.label}</strong> — {x.detail}</li>)}</ul>
                </div>
              ) : null}
            </>
          )
        })() : null}
      </Drawer>
    </section>
  )
}
